import type { AccessInfo } from './use-access'

/**
 * Capability predicates shared by a page AND the product walkthrough's copy, so
 * what a tour says a user can do is decided by the same function that decides
 * what the page renders. Drift between the two becomes a compile-time change in
 * one place instead of a review catch.
 */

/** Client record billing tabs (Timesheet, Invoices, Contracts & pricing) — client-details.tsx. */
export const canViewClientBilling = (a: AccessInfo): boolean => a.can('invoicing', 'read')

/** Activity Log "Technical" tab and per-event metadata — activity-log-panel.tsx. */
export const canSeeTechnicalLog = (a: AccessInfo): boolean => a.can('chart_queue', 'read', 'all')

/**
 * Coder Performance team view (pick and compare coders); everyone else gets
 * their own numbers only — use-coder-performance-filters.tsx, same rule as the
 * backend. NOT `canAdminClient` (coders hold charts_dashboard:read:own).
 */
export const isPerformanceAdmin = (a: AccessInfo): boolean =>
  a.can('charts_dashboard', 'edit', 'own') || a.hasCrossClientAccess

/**
 * Every write on the /clients pages — Add Client, Edit Details, client status,
 * facility / provider / user add, edit, specialties and status. Mirrors
 * requireClientsDirWrite: `clients:edit:all`, so who may write is DB data
 * (super_admin and fde_solutions_engineer today). client_admin holds only :own
 * and sees the pages read-only; buttons are hidden, not disabled. Impersonate is
 * NOT included — that stays super admin only (isSuperAdmin).
 */
export const canManageClients = (a: AccessInfo): boolean => a.can('clients', 'edit', 'all')

/** Super Admin only (`system_admin:edit:all`) — impersonation, assigning admin-level roles. */
export const isSuperAdmin = (a: AccessInfo): boolean => a.can('system_admin', 'edit', 'all')

/**
 * Created By / Updated By columns on the /clients pages (and the fields behind
 * them). Internal staff only: System Admin read, or clients read at scope 'all'
 * (super_admin and the cross-client engineer roles). client_admin and coder
 * never qualify. Same rule as the server's `canSeeAuditActors` in
 * system-admin.routes.ts, which omits the fields for everyone else.
 */
export const canSeeAuditActors = (a: AccessInfo): boolean =>
  a.can('system_admin', 'read', 'all') || a.can('clients', 'read', 'all')

/** The header's global search box (clinical results only) — header.tsx. */
export const canSearchClinical = (a: AccessInfo): boolean =>
  a.can('chart_queue', 'read') || a.can('knowledge_base', 'read')

/**
 * Chart-ID search inside the header search panel: pasted lists of Claim ID /
 * Patient ID / Med ID and the Facility / Status / Match chart filters. Charts
 * only, and every row opens /workspace/queue/:id, so it needs chart read — the
 * same gate as the backend's /search/advanced routes.
 */
export const canAdvancedChartSearch = (a: AccessInfo): boolean => a.can('chart_queue', 'read')

/** Reply to a note in the Team Notes feed — notes.tsx (POST /notes/:id/reply needs notes edit). */
export const canReplyToNotes = (a: AccessInfo): boolean => a.can('notes', 'edit')

/** Write a note on a chart (the notes drawer composer) — notes-drawer.tsx. */
export const canWriteChartNotes = (a: AccessInfo): boolean => a.can('chart_queue', 'edit')

/** Add / edit / deactivate mandatory-field rules — the server's platform-config write gate. */
export const canEditMandatoryFields = (a: AccessInfo): boolean => a.can('mandatory_fields', 'edit')

/**
 * Edit the archive details of an already-archived chart — the correction
 * action on History › Archived.
 *
 * Super Admin only: `system_admin:edit:all`, held by super_admin alone. NOT
 * `history:edit:all` — measured on local-dev that is held by five roles
 * (incl. fde_solutions_engineer), which would hand billing-classification
 * corrections to engineers. NOT `chart_queue:edit:own` or `history:edit:own`
 * either: archiving must not imply revising a finished decision. Mirrors
 * requirePermission('system_admin','edit','all') on
 * PATCH /workspace/queue/:id/archive.
 */
export const canCorrectArchive = (a: AccessInfo): boolean => a.can('system_admin', 'edit', 'all')

/**
 * Manage the Knowledge Base archive-reason catalogue (KB › Archive Reasons).
 *
 * `system_admin:edit:all` — super_admin only — rather than the
 * `knowledge_base:edit:all` every other KB catalogue uses. These rows set
 * `billable_default`, so the write surface is narrower than the rest of the KB
 * on purpose. Mirrors requirePermission('system_admin','edit','all') on the
 * /kb/archive-reasons write routes. Reads stay open to everyone.
 */
export const canManageArchiveReasons = (a: AccessInfo): boolean =>
  a.can('system_admin', 'edit', 'all')
